/**
 * ProjectScopeGate
 *
 * Shared wrapper for the project sub-pages (Overview / Messages / Runs /
 * Results). Reads the selected project from the context bar, loads it, and
 * handles the shared empty/loading/error/draft states so each sub-page only
 * has to render its own content given a loaded project.
 */

import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Flex, useTheme, spacing } from '@fti/design-system';
import { useProject } from '../../../api/media-sim/projects';
import { useProjectRun } from '../../../components/media-sim/layout/ProjectRunContext';
import type { Project } from '../../../types/media-sim';

interface ProjectScopeGateProps {
  /** Renders once a non-draft project is loaded. */
  children: (project: Project) => ReactNode;
}

export function ProjectScopeGate({ children }: ProjectScopeGateProps) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { selectedProjectId } = useProjectRun();
  const { data: project, loading, error } = useProject(selectedProjectId || '');

  const message = (text: string) => (
    <Card variant="outlined">
      <Card.Body>
        <Flex justify="center" style={{ padding: spacing.lg }}>
          <p style={{ color: theme.colors.text.secondary, margin: 0 }}>{text}</p>
        </Flex>
      </Card.Body>
    </Card>
  );

  if (!selectedProjectId) {
    return message('Select a project in the context bar above to get started.');
  }
  if (loading) return message('Loading project...');
  if (error || !project) {
    return (
      <Card variant="outlined">
        <Card.Body>
          <p style={{ color: theme.colors.semantic.error.default, margin: 0 }}>
            {error?.message || 'Project not found'}
          </p>
        </Card.Body>
      </Card>
    );
  }

  // Draft projects belong in the wizard, matching the previous detail-page behavior.
  if (project.status === 'draft') {
    navigate(`/simulations/projects/${project.id}/edit`, { replace: true });
    return null;
  }

  return <>{children(project)}</>;
}
